import onetime from "@-/com.npmjs.onetime";
import { createBoundedSerialQueue } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * A completion is one local lookup plus one insert/update of a `user_slide` row: milliseconds on a
 * healthy database, and still well inside this on a busy one. A completion that outlives it is
 * wedged underneath (a connection lock, a closed handle), not slow.
 */
export const SLIDE_COMPLETION_RUN_DEADLINE_MS = 15_000;

/**
 * A completion waits in line behind at most a few others, each bounded by the run deadline above.
 * Swiping through several slides quickly queues one per slide.
 */
export const SLIDE_COMPLETION_WAIT_DEADLINE_MS = 60_000;

// Completions and their undos are serialized, so an undo always runs after the completion it takes
// back, and rapid swipes write in order. Completions are independent and idempotent (an existing row
// is resurrected, never duplicated), so a straggler overlapping the next completion is harmless: the
// line ADVANCES past a completion that overruns instead of parking every later one behind it.
const getSlideCompletionQueue = onetime(() =>
  createBoundedSerialQueue({
    name: "slide completion",
    onOverrun: "advance",
    runDeadline: SLIDE_COMPLETION_RUN_DEADLINE_MS,
    waitDeadline: SLIDE_COMPLETION_WAIT_DEADLINE_MS,
  }),
);

export function enqueueSlideCompletion<T>(task: () => Promise<T>): Promise<T> {
  return getSlideCompletionQueue().run("complete slide", task);
}
